// Pharmascience — conditional loader for the GSAP motion module.
//
// 🔴 WHY THIS FILE EXISTS. GSAP is 367KB of source / ~42KB gzipped, and it was
// making the main bundle 8x bigger (5.7KB -> 48KB gzipped) on EVERY page —
// including the deep topic pages where a learner does the actual reading and
// where NOTHING is animated. Measured attribution of the pre-split bundle:
//
//   gsap-core.js      167.7 KB
//   ScrollTrigger.js  109.9 KB
//   CSSPlugin.js       63.6 KB   (drives opacity/transform/width)
//   Observer.js        25.7 KB   (ScrollTrigger's own dependency)
//   -------------------------
//   367 KB of 380 KB total source
//
// Tree-shaking cannot help: all four are load-bearing (verified — Draggable,
// MotionPath, Physics2D, CSSRulePlugin are already absent from the bundle). The
// only real lever is not shipping them where they do nothing, which is what
// this does.
//
// The selector list below is the ONLY duplication introduced. It is a coarse
// "does this page animate at all" gate; gsap-motion.js still re-queries each
// group precisely and no-ops independently. A stale entry here degrades to
// loading GSAP unnecessarily (or skipping motion), never to broken content —
// the un-enhanced page is already correct, which is the invariant that makes
// this safe.

/*
 * Every element gsap-motion.js can animate. Keep in sync with the selectors in
 * heroEntrance / scrollReveals / progressFills / countUps.
 */
const MOTION_SELECTORS = [
  // heroEntrance selectors REMOVED 2026-08-25 (FE-2) — the hero entrance is
  // pure CSS now (_hero.scss, `html.js .bnrmp-hero`), so GSAP must NOT be
  // fetched on its account. Leaving them here would have loaded 42KB on the
  // anonymous landing page to run a function that no longer does anything.
  // The landing page still fetches the chunk IF it carries a scrollReveals
  // section (.bnrmp-features / .bnrmp-cta / .bnrmp-faq / .bnrmp-cert-tabs),
  // which it does — so this is not yet a saving on that page, it is a
  // correctness fix. A page with ONLY a hero now correctly skips GSAP.
  // scrollReveals
  '.bnrmp-features',
  '.bnrmp-cta',
  '.bnrmp-faq',
  '.bnrmp-cert-tabs',
  // progressFills
  '.bnrmp-progress__fill',
  // countUps
  '.bnrmp-dashboard-progress__percent',
  '.bnrmp-dashboard-progress__card-percent',
].join(',');

export function initMotion() {
  /*
   * Reduced motion short-circuits BEFORE the network request. Every animation
   * in gsap-motion.js is skipped under `reduce` anyway, so fetching 42KB to
   * then do nothing is pure waste for exactly the users least likely to want
   * it. WCAG 2.3.3 outcome is unchanged — content already renders final.
   */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  if (!document.querySelector(MOTION_SELECTORS)) return;

  /*
   * Failure here is non-fatal by construction: no inline opacity:0 has been
   * written yet (gsap-motion.js writes it only once loaded, via fromTo), so a
   * failed chunk fetch — offline, a hospital proxy stripping the request, a bad
   * deploy — leaves the page at its correct, fully visible server-rendered
   * state. This is the same property that makes the no-JS path safe.
   */
  import(/* webpackChunkName: "motion" */ './gsap-motion.js')
    .then((m) => m.runGsapMotion())
    .catch(() => {});
}
